import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import useSoundStore from "../store/useSoundStore";
import { FaEllipsisV, FaEdit, FaTrash } from "react-icons/fa";

const SavedMenu = ({ sounds, playMix }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [editComboId, setEditComboId] = useState(null);
  const [comboName, setComboName] = useState("");
  const [savedCombos, setSavedCombos] = useState([]);
  const [activeComboId, setActiveComboId] = useState(null);
  const [playingCombo, setPlayingCombo] = useState(null);

  const inputRef = useRef(null);
  const comboListRef = useRef(null);
  const { resetSignal, playingSounds } = useSoundStore();

  const generateId = (len = 8) =>
    Array(len)
      .fill(0)
      .map(() => "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)])
      .join("");

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("savedCombos")) || [];
    setSavedCombos(stored);
  }, []);

  useEffect(() => {
    if (searchParams.get("save") === "true") {
      setCreating(true);
      setTimeout(() => {
        searchParams.delete("save");
        setSearchParams(searchParams);
      }, 100);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setPlayingCombo(null);
    setActiveComboId(null);
  }, [resetSignal]);

  const handleClickOutside = useCallback(
    (e) => {
      if (
        creating &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        handleSave();
        setCreating(false);
        setEditComboId(null);
      }

      if (
        activeComboId &&
        comboListRef.current &&
        !comboListRef.current.contains(e.target)
      ) {
        setActiveComboId(null);
      }
    },
    // eslint-disable-next-line
    [creating, activeComboId, comboName]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  const handleSave = () => {
    if (!comboName.trim()) return;

    const newCombo = playingSounds.map((index) => ({
      index,
      volume: sounds[index]?.volume() ?? 1,
    }));

    const updatedCombos = editComboId
      ? savedCombos.map((c) =>
          c.comboId === editComboId ? { ...c, name: comboName } : c
        )
      : [
          ...savedCombos,
          {
            comboId: generateId(),
            name: comboName,
            sounds: newCombo,
            createdAt: new Date().toISOString(),
          },
        ];

    localStorage.setItem("savedCombos", JSON.stringify(updatedCombos));
    setSavedCombos(updatedCombos);
    setEditComboId(null);
    setCreating(false);
    setComboName("");

    if (!editComboId) setPlayingCombo(updatedCombos.at(-1).comboId);
  };

  const handlePlayCombo = (combo, comboId) => {
    if (playingCombo === comboId) {
      setPlayingCombo(null);
      playMix([]);
      return;
    }

    playMix(combo.sounds);
    setPlayingCombo(comboId);
  };

  const handleDelete = (id) => {
    const updated = savedCombos.filter((c) => c.comboId !== id);
    localStorage.setItem("savedCombos", JSON.stringify(updated));
    setSavedCombos(updated);
    setActiveComboId(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
  };

  const canSave = playingSounds.length > 0 && !playingCombo;

  return (
    <div className="flex flex-col items-center justify-center mt-5 w-full h-full text-center mb-6">
      {creating ? (
        <div ref={inputRef} className="mb-6 flex justify-center">
          <div className="relative flex items-center gap-3 bg-white/10 text-gray-300 px-4 py-2 rounded-full w-[260px]">
            <input
              autoFocus
              value={comboName}
              onChange={(e) => setComboName(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleSave}
              placeholder="Give a nice name"
              className="w-full bg-transparent text-white placeholder-gray-500 outline-none text-left font-thin"
            />
          </div>
        </div>
      ) : (
        <button
          onClick={() => canSave && setCreating(true)}
          className={`mb-3 px-6 py-2 rounded-full transition text-gray-500 ${savedCombos.length === 0 ? "saved-empty-save" : ""} ${
            canSave
              ? "bg-white/5 hover:scale-105 shadow-lg cursor-pointer"
              : "bg-white/10 cursor-not-allowed"
          }`}
        >
          Save Combo
        </button>
      )}

      {savedCombos.length > 0 ? (
        <div ref={comboListRef} className="flex flex-wrap gap-4 justify-center mt-4">
          {savedCombos.map((combo) => {
            const isActive = activeComboId === combo.comboId;
            const isPlaying = playingCombo === combo.comboId;
            return (
              <div
                key={combo.comboId}
                onClick={() => !isActive && handlePlayCombo(combo, combo.comboId)}
                className={`group relative flex items-center gap-3 px-10 py-2 rounded-full shadow-lg cursor-pointer transition-all ${
                  isPlaying ? "bg-white/30" : "bg-white/10"
                }`}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveComboId((prev) =>
                      prev === combo.comboId ? null : combo.comboId
                    );
                  }}
                  className="absolute left-2 text-white/70 hover:text-white opacity-70 hover:opacity-100"
                >
                  <FaEllipsisV size={12} />
                </button>

                <span
                  className={`mx-auto font-normal text-white text-center ${
                    isActive ? "invisible" : ""
                  }`}
                >
                  {combo.name}
                </span>

                {isActive && (
                  <div className="absolute flex items-center gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setComboName(combo.name);
                        setEditComboId(combo.comboId);
                        setCreating(true);
                        setActiveComboId(null);
                      }}
                      className="hover:text-white/90"
                    >
                      <FaEdit size={14} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(combo.comboId);
                      }}
                      className="hover:text-white/90"
                    >
                      <FaTrash size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="saved-empty">
          <p className="saved-empty-title">Oops! It’s quieter than a library on Mars 😶‍🌫️</p>
          <p className="saved-empty-description">Tap a few sounds, mix them like a DJ in nature’s studio 🎧🌿<br />then hit “Save Combo” to bring this empty space to life!</p>
        </div>
      )}
    </div>
  );
};

export default SavedMenu;
